import React, { useState } from 'react';
import { describeKey, outcomeLabel, dur, ms } from '../../config/healthText';
import { formatHealthTime, formatHealthFull } from '../../utils/healthTime';
import { timelineTotals } from '../../utils/healthTimeline';

const OUTCOME_CLASS = {
  recovered: 'text-teal', repaired: 'text-teal', self_recovered: 'text-teal', no_fault_confirmed: 'text-text-muted',
  gave_up: 'text-danger', open: 'text-gold', auto_closed: 'text-text-muted', recorded: 'text-text-muted',
};

const outcomesSummary = (outcomes) => Object.entries(outcomes || {})
  .filter(([, n]) => Number(n) > 0)
  .map(([o, n]) => `${n} ${outcomeLabel(o).toLowerCase()}`)
  .join(', ');

/** "silent 3.2 s · our mute 5 s" -- measured silence and our own mute, never summed. '' when neither is known. */
export function audioEvidenceLine(o) {
  const silent = ms(o?.silent_ms);
  const mute = ms(o?.self_inflicted_mute_ms);
  return [silent ? `silent ${silent}` : '', mute ? `our mute ${mute}` : ''].filter(Boolean).join(' · ');
}

export function TimelineRow({ row, ctx }) {
  const [open, setOpen] = useState(false);
  const title = describeKey(row.title_key ?? row.template_key, row.params, ctx);

  if (row.kind === 'marker') {
    return (
      <div className="text-xs text-text-muted pl-1 py-0.5" data-testid="timeline-marker">
        <span className="inline-block w-24 whitespace-nowrap">{formatHealthTime(row.started_at, ctx.tz, ctx.now)}</span>
        <span>{title}</span>
      </div>
    );
  }

  const many = Number(row.count) > 1;
  const start = formatHealthTime(row.started_at, ctx.tz, ctx.now);
  const end = row.ended_at ? formatHealthTime(row.ended_at, ctx.tz, ctx.now) : '';
  const when = many && end && end !== start ? `${start} – ${end}` : start;
  const occ = Array.isArray(row.occurrences) ? row.occurrences : [];
  const outcome = many ? outcomesSummary(row.outcomes) : outcomeLabel(occ[0]?.outcome);
  const topOutcome = occ[0]?.outcome;
  return (
    <div className="rounded-xl border border-white/[0.08]" data-testid="timeline-incident">
      <button className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left" onClick={() => setOpen((o) => !o)}>
        <span className="text-sm text-text-main">
          {open ? '▾' : '▸'} {title}
          {many ? <span className="text-text-muted"> ×{row.count}</span> : null}
          <span className="text-xs text-text-muted ml-2">{when}</span>
          {audioEvidenceLine(row) ? <span className="text-xs text-text-muted ml-2" data-testid="audio-evidence">{audioEvidenceLine(row)}</span> : null}
        </span>
        <span className={`text-xs whitespace-nowrap ${many ? 'text-text-muted' : (OUTCOME_CLASS[topOutcome] ?? 'text-text-muted')}`}>
          {outcome}{dur(row.duration_s) ? ` · ${dur(row.duration_s)}` : ''}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 text-xs text-text-muted space-y-2">
          {occ.map((o, i) => (
            <div key={o.correlation_id ?? i} data-testid="timeline-occurrence">
              <p className="text-text-main">
                {formatHealthTime(o.started_at, ctx.tz, ctx.now)}
                {' — '}
                <span className={OUTCOME_CLASS[o.outcome] ?? ''}>{outcomeLabel(o.outcome)}</span>
                {dur(o.duration_s) ? ` · ${dur(o.duration_s)}` : ''}
                {audioEvidenceLine(o) ? ` · ${audioEvidenceLine(o)}` : ''}
              </p>
              {(o.steps ?? []).map((s, j) => (
                <div key={j} className="flex gap-3 pl-3">
                  <span className="w-28 shrink-0" title={formatHealthFull(s.time, ctx.tz)}>{formatHealthTime(s.time, ctx.tz, ctx.now)}</span>
                  <span className="text-text-main">{describeKey(s.key, s.params, ctx)}</span>
                </div>
              ))}
              {(o.steps ?? []).length === 0 && <p className="pl-3">No steps recorded.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const dayTitle = (d) => {
  if (d.is_today) return 'Today';
  if (d.is_yesterday) return 'Yesterday';
  return d.date;
};

export default function Timeline({ timeline, timelineDays, ctx }) {
  const rows = Array.isArray(timeline) ? timeline : [];
  const days = Array.isArray(timelineDays) && timelineDays.length ? timelineDays : null;
  const totals = timelineTotals(rows, ctx.tz, ctx.now);
  return (
    <div data-testid="timeline">
      <p className="text-xs text-text-muted mb-2" data-testid="timeline-totals">
        Today: {totals.incidents} incident{totals.incidents === 1 ? '' : 's'}
      </p>
      {rows.length === 0 ? <p className="text-sm text-text-muted">No incidents.</p> : days ? (
        <div className="space-y-3">
          {days.map((d) => (
            <div key={d.date} data-testid="timeline-day">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1" data-testid="timeline-day-header">
                {dayTitle(d)}{d.is_today || d.is_yesterday ? <span className="font-normal normal-case ml-2">{d.date}</span> : null}
              </p>
              <div className="space-y-2">
                {(d.rows ?? []).map((r, i) => <TimelineRow key={`${r.correlation_id ?? r.started_at}-${i}`} row={r} ctx={ctx} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => <TimelineRow key={`${r.correlation_id ?? r.started_at}-${i}`} row={r} ctx={ctx} />)}
        </div>
      )}
    </div>
  );
}
